import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import HeroSection from './components/HeroSection/HeroSection';
import StatsSection from './components/StatsSection/StatsSection';
import ContractExpirationsSection from './components/ContractExpirationsSection/ContractExpirationsSection';
import ANDAUpdatesSection from './components/ANDAUpdatesSection/ANDAUpdatesSection';
import PreSolicitationAlertsSection from './components/PreSolicitationAlertsSection/PreSolicitationAlertsSection';
import LossOfExclusivitySection from './components/LossOfExclusivitySection/LossOfExclusivitySection';
import LandingPageSkeleton from './components/LandingPageSkeleton/LandingPageSkeleton';
import { useLandingPageLogic } from './useLandingPage.logic';
import '../global.styles.scss';
import './landingPage.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const ProductWikiLandingPage: React.FC = observer(() => {
  const { landingPageData, loading } = useLandingPageLogic();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate wrapper height based on scaled content to prevent extra white space
  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current || loading || !landingPageData) return;

    const updateWrapperHeight = () => {
      // Use requestAnimationFrame to ensure calculation happens after render
      requestAnimationFrame(() => {
        if (!contentRef.current || !wrapperRef.current) return;

        const contentHeight = contentRef.current.scrollHeight || 0;
        // Since content is scaled to 65%, the visual height is 65% of the scrollHeight
        const scaledHeight = contentHeight * 0.65;
        wrapperRef.current.style.height = `${scaledHeight}px`;
      });
    };

    // Initial calculation with a small delay to ensure content is rendered
    const timeoutId = setTimeout(updateWrapperHeight, 100);

    // Update on window resize
    window.addEventListener('resize', updateWrapperHeight);

    // Use ResizeObserver to watch for content changes
    const resizeObserver = new ResizeObserver(() => {
      updateWrapperHeight();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWrapperHeight);
      resizeObserver.disconnect();
    };
  }, [loading, landingPageData]);

  if (loading) {
    return <LandingPageSkeleton />;
  }

  if (!landingPageData) {
    return (
      <div className="landing-page-wrapper" ref={wrapperRef}>
        <div className="landing-page" ref={contentRef}>
          <div
            className="container d-flex justify-content-center align-items-center"
            style={{ minHeight: '100vh' }}
          >
            <div className="text-center text-white">
              <h2>Error loading data</h2>
              <p>Please try refreshing the page.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page-wrapper" ref={wrapperRef}>
      <div className="landing-page" ref={contentRef}>
        <HeroSection suggestions={landingPageData.searchSuggestions} />
        <main className="container">
          <StatsSection stats={landingPageData.stats} />
          <ContractExpirationsSection contracts={landingPageData.contractExpirations} />
          <ANDAUpdatesSection andaUpdates={landingPageData.andaUpdates} />
          <PreSolicitationAlertsSection alerts={landingPageData.preSolicitationAlerts} />
          <LossOfExclusivitySection lossOfExclusivity={landingPageData.lossOfExclusivity} />
          {/* <MarketIntelligenceSection marketIntelligence={landingPageData.marketIntelligence} /> */}
        </main>
      </div>
    </div>
  );
});

export default ProductWikiLandingPage;
